import {
  CompareConfigItem,
  isNotEqual,
  optionalStringsDiffer,
  regionIdsDiffer,
  useGeoEvent,
  UseGeoEventEditableResult,
} from 'features/geoEvents/hooks/useGeoEvent';
import {
  Wildfire,
  WildfireCreateUpdateData,
} from 'features/wildfires/wildfire.types';

export type UseWildfireGeoEventProps = {
  id?: number;
  enabled?: boolean;
};

export type UseWildfireGeoEvent = UseGeoEventEditableResult<
  Wildfire,
  WildfireCreateUpdateData
>;

export const wildfireFieldPatchConfigs: Record<string, CompareConfigItem> = {
  address: {
    shouldPatch: optionalStringsDiffer,
  },
  reporterNotes: {
    shouldPatch: isNotEqual,
  },
};

/**
 * Wildfire-specific data field configurations for constructPatch
 */
export const wildfireDataPatchConfigs: Record<
  keyof WildfireCreateUpdateData['data'],
  CompareConfigItem
> = {
  acreage: {
    shouldPatch: isNotEqual,
  },
  containment: {
    shouldPatch: isNotEqual,
  },
  isComplexParent: {
    shouldPatch: isNotEqual,
  },
  isFps: {
    shouldPatch: isNotEqual,
  },
  isPrescribed: {
    shouldPatch: isNotEqual,
  },
  manualDeactivationStarted: {
    shouldPatch: isNotEqual,
  },
  temporarilyDisplayEvacZones: {
    shouldPatch: isNotEqual,
  },
  hasCustomEvacuationOrders: {
    shouldPatch: isNotEqual,
  },
  hasCustomEvacuationWarnings: {
    shouldPatch: isNotEqual,
  },
  hasCustomEvacuationAdvisories: {
    shouldPatch: isNotEqual,
  },
  hasCustomEvacuationShelterInPlace: {
    shouldPatch: isNotEqual,
  },
  evacuationNotes: {
    shouldPatch: optionalStringsDiffer,
  },
  evacuationAdvisories: {
    shouldPatch: optionalStringsDiffer,
  },
  evacuationWarnings: {
    shouldPatch: optionalStringsDiffer,
  },
  evacuationOrders: {
    shouldPatch: optionalStringsDiffer,
  },
  evacuationShelterInPlace: {
    shouldPatch: optionalStringsDiffer,
  },
  links: {
    shouldPatch: isNotEqual,
  },
  prescribedDateStartLocal: {
    shouldPatch: optionalStringsDiffer,
  },
  reporterOnlyNotes: {
    shouldPatch: optionalStringsDiffer,
  },
  internalLinks: {
    shouldPatch: isNotEqual,
  },
  urgency: {
    shouldPatch: isNotEqual,
  },
  unstructuredEvacuationRegionIds: {
    shouldPatch: regionIdsDiffer,
  },
  // These fields are never sent with PATCH
  geoEventType: { shouldPatch: () => false },
  prescribedDateStart: { shouldPatch: () => false },
  reporterOnlyNotesDateModified: { shouldPatch: () => false },
  // Not editable in WildfireForm; omitting it keeps any existing locks.
  botLockedFields: { shouldPatch: () => false },
};

export const useWildfireGeoEvent = (
  props: UseWildfireGeoEventProps,
): UseWildfireGeoEvent => {
  return useGeoEvent<Wildfire, WildfireCreateUpdateData>({
    ...props,
    dataPatchConfigs: wildfireDataPatchConfigs,
    fieldPatchConfigs: wildfireFieldPatchConfigs,
  });
};
